import DesktopOnly from '@components/Common/DesktopOnly';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Button, CircularProgress, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { formatSavedAt, type SaveState } from './useAutosave';
import { hasBlocker, type ValidationItem } from './types';
import './index.scss';

interface StateAction {
  label: string;
  onClick: () => void;
  /** Disabled for reasons other than validation, e.g. a save in flight. */
  disabled?: boolean;
  busy?: boolean;
  /**
   * Run even when the validation bar shows a blocker.
   *
   * Blockers exist to stop something going live. An action that takes content DOWN is the exact
   * opposite, and gating it on the same list is backwards: a guide that breaks the house rules is
   * precisely the one an operator most needs to unpublish, and it was the one this refused to
   * unpublish. Archiving was unreachable for that reason alone.
   */
  bypassBlockers?: boolean;
}

interface EditorShellProps {
  /** Back link to the parent list. */
  backLabel: string;
  onBack: () => void;
  /** The artefact's name, not the page's. */
  name: string;
  /** Status chip — DRAFT / ACTIVE / PUBLISHED / ARCHIVED. */
  status?: string;
  statusTone?: string;
  statusTint?: string;

  /** Autosave reporting. */
  saveState?: SaveState;
  /** Shown in place of the autosave timestamp when this shell saves explicitly. */
  unsavedLabel?: string;
  savedAt?: number | null;

  onPreview?: () => void;
  previewLabel?: string;
  /**
   * Give Preview an icon and it collapses to an icon button (label in the tooltip) below the
   * wide breakpoint, which is what lets the header fit at the editor's 1024px floor. Without one
   * it keeps its label at every width, for editors whose preview label is a state, not an action.
   */
  previewIcon?: React.ReactElement;
  /** An extra header action, e.g. "Send test" on the email editor. */
  secondaryAction?: StateAction;
  /** The ONE state change: Publish / Unpublish / Send. Never a third. */
  stateAction?: StateAction;

  validation?: ValidationItem[];
  inspector: React.ReactNode;
  children: React.ReactNode;

  /** Shown in the redirect card below 1024px. */
  redirectTitle: string;
  redirectReason: string;
}

/** Where the inspector docks beside the canvas; below it, it overlays. Matches the CSS. */
const INSPECTOR_DOCKED_QUERY = '(min-width: 1200px)';

/**
 * The editor shell — a third page layout alongside the CRM list and the entity
 * workspace, for surfaces that produce an ARTEFACT rather than edit a record.
 *
 * Explicitly desktop-only. Below 1024px it renders the redirect card instead of
 * a half-working rich-text editor.
 */
const EditorShell: React.FC<EditorShellProps> = ({
  backLabel,
  onBack,
  name,
  status,
  statusTone = 'var(--amber)',
  statusTint = 'var(--amber-tint)',
  saveState = 'idle',
  savedAt = null,
  onPreview,
  previewLabel = 'Preview',
  previewIcon,
  secondaryAction,
  stateAction,
  validation = [],
  inspector,
  children,
  redirectTitle,
  redirectReason,
}) => {
  /*
   * The inspector starts open only where it fits beside the canvas.
   *
   * At 1024px the content column is ~790px once the sidebar is drawn, and a 640px canvas floor
   * plus a 320px inspector is 960: the page scrolled sideways, the inspector was cut off at the
   * right and Save / Archive were pushed off-screen. Below the wide breakpoint the inspector is
   * now closed by default and, when opened, overlays the canvas rather than squeezing it.
   */
  const wide = useMediaQuery(INSPECTOR_DOCKED_QUERY, { noSsr: true });
  const [inspectorOpen, setInspectorOpen] = useState(wide);
  useEffect(() => setInspectorOpen(wide), [wide]);

  const blocked = hasBlocker(validation);
  const blockers = validation.filter((v) => v.level === 'blocker');
  const warnings = validation.filter((v) => v.level === 'warning');
  // Blockers outrank warnings — the bar states the thing that stops publish.
  const shown = blockers.length ? blockers : warnings;
  const level = blockers.length ? 'blocker' : 'warning';

  const gatedByBlockers = blocked && !stateAction?.bypassBlockers;
  const actionDisabled = gatedByBlockers || stateAction?.disabled || stateAction?.busy;
  const disabledReason = gatedByBlockers
    ? blockers.length === 1
      ? blockers[0].message
      : `${blockers.length} problems must be resolved before publishing`
    : '';

  /* Theme buttons, not hand-rolled ones: the header and the inspector draw from one spec, so a
     header action and an inspector action no longer differ in tracking and weight. */
  const stateButton = stateAction ? (
    <Button variant="contained" onClick={stateAction.onClick} disabled={actionDisabled}>
      {stateAction.busy ? <CircularProgress size={13} color="inherit" /> : stateAction.label}
    </Button>
  ) : null;

  const previewButton = !onPreview ? null : previewIcon && !wide ? (
    <Tooltip title={previewLabel}>
      <IconButton aria-label={previewLabel} className="cc-ed__icon-btn" onClick={onPreview}>
        {previewIcon}
      </IconButton>
    </Tooltip>
  ) : (
    <Button variant="outlined" startIcon={previewIcon} onClick={onPreview}>
      {previewLabel}
    </Button>
  );

  return (
    <DesktopOnly title={redirectTitle} reason={redirectReason} recordLabel={name} minWidth={1024}>
      <Box className="cc-ed">
        {/* ── Header. Two actions, never three: autosave owns save-and-stay. ── */}
        <Box className="cc-ed__header cc-scroll-x">
          <button type="button" className="cc-ed__back" onClick={onBack}>
            <ArrowBackIcon className="cc-ed__back-icon" />
            {backLabel}
          </button>
          <span className="cc-ed__divider" />
          <span className="cc-ed__name" title={name}>
            {name || 'Untitled'}
          </span>
          {status && (
            <span
              className="cc-ed__status"
              style={{ backgroundColor: statusTint, color: statusTone }}
            >
              {status}
            </span>
          )}
          <span className="cc-ed__saved">
            {saveState === 'saving'
              ? 'saving…'
              : saveState === 'error'
                ? 'save failed'
                : formatSavedAt(savedAt)}
          </span>

          <Box className="cc-ed__header-spacer" />

          {previewButton}
          {secondaryAction && (
            <Button
              variant="outlined"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled || secondaryAction.busy}
            >
              {secondaryAction.busy ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                secondaryAction.label
              )}
            </Button>
          )}
          {/* The reason lives on hover — a disabled button with no explanation
              is the most common way an author gets stuck here. */}
          {stateAction &&
            (gatedByBlockers ? (
              <Tooltip title={disabledReason}>
                <span>{stateButton}</span>
              </Tooltip>
            ) : (
              stateButton
            ))}

          <Tooltip title={inspectorOpen ? 'Hide inspector' : 'Show inspector'}>
            <button
              type="button"
              className={`cc-ed__inspector-toggle${inspectorOpen ? ' is-active' : ''}`}
              onClick={() => setInspectorOpen((v) => !v)}
              aria-label="Toggle inspector"
              aria-expanded={inspectorOpen}
            >
              <ViewSidebarOutlinedIcon fontSize="small" />
            </button>
          </Tooltip>
        </Box>

        {/* ── Canvas + inspector ── */}
        <Box className="cc-ed__body">
          <Box className="cc-ed__canvas-col">
            <Box className="cc-ed__canvas">{children}</Box>

            {/* Validation is persistent STATE, not an event, so it is a pinned
                bar rather than a toast that scrolls away and cannot be recalled. */}
            {shown.length > 0 && (
              <Box
                className={`cc-ed__validation cc-ed__validation--${level}`}
                role="status"
                aria-live="polite"
              >
                {level === 'blocker' ? (
                  <ErrorOutlineIcon className="cc-ed__validation-icon" />
                ) : (
                  <WarningAmberOutlinedIcon className="cc-ed__validation-icon" />
                )}
                <Box className="cc-ed__validation-list">
                  {shown.map((item) => (
                    <Box key={item.id} className="cc-ed__validation-row">
                      <span className="cc-ed__validation-text">{item.message}</span>
                      {item.onFix && item.fixLabel && (
                        <button
                          type="button"
                          className="cc-ed__validation-fix"
                          onClick={item.onFix}
                        >
                          {item.fixLabel}
                        </button>
                      )}
                    </Box>
                  ))}
                </Box>
                {level === 'blocker' && warnings.length > 0 && (
                  <span className="cc-ed__validation-more">
                    +{warnings.length} warning{warnings.length === 1 ? '' : 's'}
                  </span>
                )}
              </Box>
            )}
          </Box>

          {inspectorOpen && (
            <Box className={`cc-ed__inspector${wide ? '' : ' cc-ed__inspector--overlay'}`}>
              <Box className="cc-ed__inspector-head">Inspector</Box>
              <Box className="cc-ed__inspector-body">{inspector}</Box>
            </Box>
          )}
        </Box>
      </Box>
    </DesktopOnly>
  );
};

export default EditorShell;
